import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import jss, { Rule, StyleSheet } from 'jss';
import { interval, timer } from 'rxjs';
import { debounceTime, filter, takeWhile, tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import {
    ConfigPath,
    ConfigPathLevel,
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    createLevelPath,
    findLevel,
    HierarchyUserInfoItem,
    Metric,
    PillarsInGrid,
    QuickSwitchItem,
    RoleHierarchyItem,
} from '../insight/config.insight.model';
import {
    autoSizeColumnTree,
    Column,
    Controller,
    DivRenderer,
    DivRendererOptions,
    flattenArrayWithObj,
    GridNode,
    inverseSortOrder,
    RowNode,
    Sort,
    SortOrder,
} from '../insight/experimental.grid';
import {
    ColumnContext,
    ColumnID,
    ColumnType,
    concatLevelDataMap,
    DataSource,
    GridInsightNewTabClickEvent,
    GridInsightSelectionChangeEvent,
    GridInsightSelectionItem,
    Refs,
    RowContext,
    RowId,
} from '../insight/grid.insight.model';
import {
    ColumnElementComponent,
    DefaultCellComponent,
    DefaultGridComponent,
    DefaultRowComponent,
    MetricElementComponent,
    NodeElementComponent,
    render,
} from '../insight/grid.insight.render';
import {
    gridRetrieveData,
    gridRetrieveDataCancel,
    gridSort,
    gridToggleColumn,
} from '../insight/insight.action';
import { InsightEpicBridge } from '../insight/insight.epic.bridge';
import { GridState } from '../insight/insight.state';
import { OptionalColumn } from '../insight/optional-column.insight.model';
import { CacheDateItem } from '../insight/performance-data/cache-date.pd.model';
import { LevelDataMap } from '../insight/performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../insight/performance-data/metric.pd.model';
import {
    getEventDateValue,
    PeriodTypeOpts,
} from '../insight/period.insight.model';
import store, { StateToProps } from '../store';
import { disuseStore, useStore } from '../store.decorator';
import { User } from '../user/user.model';
import { PeriodType } from '../widget/date-range.widget.model';

import { createTab, HiTabConfig } from './human-insight.model';
import { SuperGroupExtra } from './super-group.hi.model';

// TODO de-dup and merge into epics
@Component({
    selector: '[cmpsHumanInsightGrid]',
    templateUrl: './grid.hi.component.html',
})
export class GridHumanInsightComponent
    implements
        OnChanges,
        OnInit,
        AfterViewInit,
        OnDestroy,
        StateToProps<GridState>
{
    state: GridState;

    @ViewChild('container', { static: true })
    private readonly _container: ElementRef<HTMLElement>;

    @ViewChild('defaultGrid', { static: true })
    private readonly _defaultGrid: DefaultGridComponent;

    @ViewChild('defaultRow', { static: true })
    private readonly _defaultRow: DefaultRowComponent;

    @ViewChild('defaultCell', { static: true })
    private readonly _defaultCell: DefaultCellComponent;

    @ViewChild('column', { static: true })
    private readonly _column: ColumnElementComponent;

    @ViewChild('node', { static: true })
    private readonly _node: NodeElementComponent;

    @ViewChild('metric', { static: true })
    private readonly _metric: MetricElementComponent;

    @Input() config: HiTabConfig;
    @Input() view: ConfigView;
    @Input() metrics: Metric[];
    @Input() periodType: PeriodType;
    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Input() superGroup: QuickSwitchItem<SuperGroupExtra>;
    @Input() viewPillar: ConfigViewPillar;
    @Input() viewMetric: ConfigViewMetric;
    @Input() path: ConfigPath;
    @Input() roleHierarchy: RoleHierarchyItem[];
    @Input() pillars: ConfigViewPillar[];
    @Input() pillarSelection: ConfigViewPillar[];
    @Input() columns: OptionalColumn[];
    @Input() columnSelection: OptionalColumn[];
    @Input() maxSelection: number;
    @Input() configUserInfo: HierarchyUserInfoItem;
    @Input() className: string;
    @Input() maxHeight: number;
    @Input() trendVisible: boolean;
    @Input() trendHideFirstGridColumn: boolean;
    @Output() layout: EventEmitter<string>;
    @Output() newTabClick: EventEmitter<GridInsightNewTabClickEvent>;

    @Output() selectionChange: EventEmitter<GridInsightSelectionChangeEvent>;

    localState: {
        numSelected?: number;
        dismissedMaxSelection?: boolean;
    };

    /** @private */
    _rootRowId: string;

    private readonly _refs: Refs;

    private _renderer: DivRenderer;
    private _grid: GridNode;
    private _dataSource: DataSource;
    private readonly _sheet: StyleSheet;
    private readonly _contentRule: Rule;

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _epicBridge: InsightEpicBridge,
    ) {
        this.layout = new EventEmitter();
        this.newTabClick = new EventEmitter();
        this.selectionChange = new EventEmitter();

        this._dataSource = new DataSource();

        this._refs = {
            translate: null,
            cmpsNumber: null,
            cmpsPercent: null,
            onExpandPillarClick: (column) => this.onExpandPillarClick(column),
            onExpandMetricClick: (column) => this.onExpandMetricClick(column),
            onExpandRowClick: (row) => this.onExpandRowClick(row),
            onSortClick: (column) => this.onSortClick(column),
            onNewTabClick: (row) => this.onNewTabClick(row),
            onToggleSelectionItemClick: (row, rootReload) =>
                this.onToggleSelectionItemClick(row, rootReload),
        };

        this._sheet = jss.createStyleSheet({}, { link: true });
        this._contentRule = this._sheet.addRule('content', {});
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this._renderer == null) {
            return;
        }

        if (changes.path != null && changes.path.previousValue !== this.path) {
            this._dataSource.path = this.path;
            this._grid.removeChildren();
            this._getData();
        }

        if (
            (changes.pillarSelection != null &&
                changes.pillarSelection.previousValue !==
                    this.pillarSelection) ||
            (changes.columnSelection != null &&
                changes.columnSelection.previousValue !== this.columnSelection)
        ) {
            const [unchanged, changed] = this._dataSource.applyHiddenColumns(
                this.pillarSelection,
                this.columnSelection,
                this._renderer,
            );
            if (
                changes.columnSelection != null &&
                changes.columnSelection.previousValue !== this.columnSelection
            ) {
                this._layoutAndAutoSize(unchanged, true);
            }
            this._layoutAndAutoSize(changed, true);
        }

        if (
            changes.trendVisible != null &&
            changes.trendVisible.previousValue !== this.trendVisible
        ) {
            this._grid.context.ctx.trendVisible = this.trendVisible;
            this._grid.forEach((e) =>
                e.cells.at(0).detectChanges({ emitEvent: false }),
            );
            // eslint-disable-next-line @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async
            timer().subscribe(() =>
                this._renderer.autoSizeColumns([ColumnID.NODE.toString()]),
            );
        }

        if (
            changes.trendHideFirstGridColumn != null &&
            changes.trendHideFirstGridColumn.previousValue !==
                this.trendHideFirstGridColumn
        ) {
            this._layout();
        }
    }

    ngOnInit(): void {
        useStore(this, 'insight.grid');
    }

    ngAfterViewInit(): void {
        this._dataSource.columnTpl = this._column.tpl;
        this._dataSource.nodeTpl = this._node.tpl;
        this._dataSource.metricTpl = this._metric.tpl;

        this._inflate();
        this._sheet.attach();
    }

    ngOnDestroy(): void {
        disuseStore(this);
        this._sheet.detach();
        this._deflate();
        this.layout.complete();
        this.newTabClick.complete();
        this.selectionChange.complete();
    }

    stateToProps(prevState?: GridState): void {
        if (prevState?.sort !== this.state.sort) {
            this._grid.context.ctx.sort = { ...this.state.sort };
            const sortDefs: Sort[] = [{ ...this.state.sort.def }];
            if (this.state.sort.def.columnId !== ColumnID.NODE.toString()) {
                sortDefs.push({
                    columnId: ColumnID.NODE.toString(),
                    order: SortOrder.ASC,
                });
            }
            if (
                !this.state.sort ||
                !this.state.sort.rowIds ||
                this.state.sort.rowIds.length === 0
            ) {
                this._grid.sort(sortDefs);
            } else {
                this._grid
                    .filter((n) => this.state.sort.rowIds.includes(n.def.id))
                    .forEach((n) =>
                        n.sort(sortDefs, null, { emitEvent: true }),
                    );
            }
            this._renderer.flatten();
            void this._renderer.layout();
        }

        // from store
        if (prevState?.pending.data !== this.state.pending.data) {
            this._dataSource.data = this.state.data;
            this._dataSource.peerGroupData = this.state.peerGroupData;
            this._dataSource.hierarchyUserInfo = this.state.hierarchyUserInfo;
            this._dataSource.hierarchyUserInfoCurrent =
                this.state.hierarchyUserInfoCurrent;
            const rowIds = Object.keys(this.state.pending.data).filter(
                (k) =>
                    prevState?.pending.data[k] && !this.state.pending.data[k],
            );
            if (
                rowIds.length > 0 &&
                this._container.nativeElement.children.length === 0
            ) {
                this._renderer.opts.height = this.maxHeight;
                this._container.nativeElement.appendChild(
                    this._grid.renderContext.element,
                );
            }
            rowIds.forEach((e) => {
                const isRoot = e === this._rootRowId;
                const row = !isRoot && this._grid.find((n) => n.def.id === e);
                if (!isRoot && !row) return;

                if (row?.context?.ctx?.cancelled) {
                    row.context.ctx.pending = false;
                    row.context.ctx.cancelled = false;
                    return;
                }

                const levels = isRoot
                    ? this.path.levels
                    : findLevel(
                          this.path.levels,
                          row.context.ctx.levelDataMap.identifier,
                      ).levels;
                let rows = this._dataSource.makeRows(
                    isRoot ? this._rootRowId : row.def.id,
                    levels,
                );

                if (row) {
                    row.context.ctx.pending = false;
                    row.context.ctx.expanded = rows.length !== 0; // TODO revise. can't tell if expand was cancelled
                    row.def.children = rows;
                    rows = [row.def];
                }

                this._grid.updateChildren(rows);

                (!row ? this._grid.children : row.children)
                    .filter((ee) => ee.context.ctx.autoExpand)
                    .forEach((ee) => this.onExpandRowClick(ee));

                if (isRoot && this._grid.length !== 0) {
                    this.onToggleSelectionItemClick(this._grid.at(0), true);
                }

                row?.cells?.at(0)?.detectChanges({ emitEvent: false });
            });
        }

        // initial expanded column is handled in _inflate()
        if (
            prevState?.lastToggleColumnState !==
            this.state.lastToggleColumnState
        ) {
            if (this.state.lastToggleColumnState) {
                if (
                    this._grid.context.ctx.pendingColumnMovementReset &&
                    this.state.lastToggleColumnState.pillarExpanded
                ) {
                    this._grid.context.ctx.pendingColumnMovementReset = false;
                    this._renderer.resetMovedColumns();
                    this._dataSource.columnsFlatten = flattenArrayWithObj(
                        this._dataSource.columns,
                        'children',
                    );
                    this._dataSource.reassignPillarHandles();
                }
                this._dataSource.applyExpandedColumnState(
                    this.state.lastToggleColumnState,
                );
            }
            const [, changed] = this._dataSource.applyHiddenColumns(
                this.pillarSelection,
                this.columnSelection,
                this._renderer,
            );
            this._layoutAndAutoSize(changed);
        }

        if (prevState?.export !== this.state.export) {
            //
        }
    }

    onExpandPillarClick(column: Column<ColumnContext>): void {
        store.dispatch(
            gridToggleColumn(column.id, !column.ctx.pillarExpanded, undefined),
        );
    }

    onExpandMetricClick(column: Column<ColumnContext>): void {
        store.dispatch(
            gridToggleColumn(column.id, undefined, !column.ctx.metricExpanded),
        );
    }

    onExpandRowClick(row: RowNode<RowContext>): void {
        if (row.context.ctx.pending) {
            row.context.ctx.pending = false;
            row.context.ctx.cancelled = true;
            store.dispatch(gridRetrieveDataCancel([row.def.id]));
        } else if (!row.context.ctx.expanded) {
            row.context.ctx.pending = true;
            this._getData(row);
        } else {
            row.context.ctx.pending = false;
            row.context.ctx.expanded = false;
            row.removeChildren();
            this._updateSelection();
        }
        row.cells.at(0).detectChanges({ emitEvent: false });
    }

    onSortClick(column: Column<ColumnContext>): void {
        const maxDepth =
            this._grid.reduce<number>(
                (p, c) => Math.max(c.context.ctx.depth, p),
                0,
            ) - 1;
        const rowIds =
            maxDepth >= 0 &&
            this._grid
                .filter((n) => n.context.ctx.depth === maxDepth)
                .map((n) => n.def.id);
        store.dispatch(
            gridSort(
                rowIds,
                inverseSortOrder(column, this.state.sort?.def, SortOrder.ASC),
            ),
        );
    }

    onNewTabClick(row: RowNode<RowContext>): void {
        if (!row.context.ctx.allowNewTab) return;
        const user = new User();
        user.id = row.context.ctx.levelDataMap.dataId;
        user.lastName = row.cells.at(0).context.ctx.value.name;
        this.newTabClick.emit({ tab: createTab(user) });
    }

    onToggleSelectionItemClick(
        row: RowNode<RowContext>,
        rootLoad?: boolean,
    ): void {
        const nodeEl = row.cells.at(0);
        const numSelected = this._grid.filter(
            (r) => r.context.ctx.selected,
        ).length;
        if (!nodeEl.context.ctx.value.unSelectable) {
            this.localState = {
                ...this.localState,
                dismissedMaxSelection: numSelected === this.maxSelection,
            };
            if (
                !row.context.ctx.selected &&
                this.maxSelection != null &&
                numSelected === this.maxSelection
            ) {
                this.localState = {
                    ...this.localState,
                    dismissedMaxSelection: false,
                };
                return;
            }
            row.context.ctx.selected = !row.context.ctx.selected;
            nodeEl.detectChanges({ emitEvent: false });
        }
        (rootLoad || !nodeEl.context.ctx.value.unSelectable) &&
            this._updateSelection();
    }

    private _getData(row?: RowNode): void {
        const root = !row;
        const identifier = root
            ? null
            : row.context.ctx.levelDataMap.identifier;
        const rowId = root
            ? RowId.create(
                  'ROOT',
                  createLevelPath(this.path.levels, 'ROOT'),
                  -1,
                  0,
                  -1,
                  -1,
                  'ROOT',
              )
            : row.def.id;
        const levelsDataMap = this._dataSource.processInputCriteriaIdTypes(row);

        if (root) {
            this._rootRowId = rowId;
            this._grid.context.ctx.rowId = this._rootRowId;
            this._grid.context.ctx.levelsDataMap = levelsDataMap;
        }

        store.dispatch(
            gridRetrieveData(
                {
                    identifier,
                    viewType: null, // dataForLevels is called, so this and related can be null
                    displayNameCriteriaIdType: null,
                    outputCriteriaIdType: null,
                    outputCriteriaIdTypes: null,
                    inputCriteriaIdTypes: null,
                    periodType: this.periodType,
                    date: this.eventDate,
                    metricIdList: this._dataSource.metrics.map(
                        (e) => e.metricId,
                    ),
                },
                rowId,
                levelsDataMap,
            ),
        );
    }

    private _updateSelection(): void {
        const selected = this._grid
            .filter((r) => r.context.ctx.selected)
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            .map(
                (r) =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({
                        rowId: r.def.id,
                        levelsDataMap:
                            this._dataSource.processInputCriteriaIdTypes(
                                r.parent instanceof RowNode ? r.parent : null,
                            ),
                        levelsDataMapRow: concatLevelDataMap(r, 0),
                        name: r.cells.at(0).context.ctx.value.name,
                    }) as GridInsightSelectionItem,
            );
        this.localState = {
            ...this.localState,
            numSelected: selected.length,
            dismissedMaxSelection: selected.length !== this.maxSelection,
        };
        this.selectionChange.emit({ selected });
    }

    private _inflate(): void {
        this._dataSource.path = this.path;
        this._dataSource.didProcessInputCriteriaIdTypes = (
            levels: ConfigPathLevel[],
            parentLevelsDataMap: LevelDataMap[],
            levelsDataMap: LevelDataMap[],
        ): void => {
            const dataIdForInputCriteriaIdType = (
                level: ConfigPathLevel,
                criteriaIdType: CriteriaIdType,
                foundLevelDataMap: LevelDataMap,
            ): number => {
                if (foundLevelDataMap) return foundLevelDataMap.dataId;
                const rh = this.roleHierarchy.find(
                    (eee) =>
                        eee.startDate &&
                        eee.endDate &&
                        this.eventDate.getTime() >= eee.startDate.getTime() &&
                        this.eventDate.getTime() <= eee.endDate.getTime() &&
                        eee.parentRoleId === this.configUserInfo.roleId,
                );
                if (criteriaIdType === CriteriaIdType.USER_ROLE_ID) {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    return this.superGroup != null &&
                        this.superGroup.extra.teamSuperGroup
                        ? // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                          rh && rh.roleId
                        : this.configUserInfo.roleId;
                }
                if (criteriaIdType === CriteriaIdType.TEAM_ROLE_ID) {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    return rh && rh.roleId;
                }
                if (
                    criteriaIdType === CriteriaIdType.USER_ID ||
                    criteriaIdType === CriteriaIdType.HIERARCHY
                ) {
                    return this.configUserInfo.userId;
                }
                if (criteriaIdType === CriteriaIdType.SUPER_GROUP_ID) {
                    return this.superGroup != null
                        ? parseInt(this.superGroup.initialCriteria)
                        : this.configUserInfo.superGroupId;
                }
                if (criteriaIdType === CriteriaIdType.TEAM_ID) {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    return this.superGroup != null &&
                        this.superGroup.extra.teamSuperGroup
                        ? this.configUserInfo.userId
                        : this.configUserInfo.managerId;
                }
            };
            levels.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                e.inputCriteriaIdTypes &&
                    e.inputCriteriaIdTypes.forEach((ee) => {
                        if (ee === CriteriaIdType.USER_ROLE_ID) {
                            const a = parentLevelsDataMap.find(
                                (eee) =>
                                    eee.outputCriteriaType ===
                                        CriteriaIdType.USER_ROLE_ID &&
                                    eee.dataId != null &&
                                    eee.displayNameCriteriaIdType ===
                                        CriteriaIdType.USER_ID,
                            );
                            const dataId = dataIdForInputCriteriaIdType(
                                e,
                                ee,
                                a,
                            );
                            dataId != null &&
                                levelsDataMap.push({
                                    identifier: e.identifier,
                                    performanceDataViewType: null, // special usage CriteriaIdType. must be null
                                    displayNameCriteriaIdType:
                                        CriteriaIdType.USER_ID,
                                    outputCriteriaType:
                                        CriteriaIdType.USER_ROLE_ID,
                                    dataId,
                                    outputCriteriaTypes: [
                                        CriteriaIdType.USER_ROLE_ID,
                                    ],
                                    outputCriteriaTypeArgs: {
                                        [CriteriaIdType.USER_ROLE_ID]: dataId,
                                    },
                                });
                        }

                        if (ee === CriteriaIdType.TEAM_ROLE_ID) {
                            const a = parentLevelsDataMap.find(
                                (eee) =>
                                    eee.outputCriteriaType ===
                                        CriteriaIdType.TEAM_ROLE_ID &&
                                    eee.dataId != null &&
                                    eee.displayNameCriteriaIdType ===
                                        CriteriaIdType.USER_ID,
                            );
                            const dataId = dataIdForInputCriteriaIdType(
                                e,
                                ee,
                                a,
                            );
                            dataId != null &&
                                levelsDataMap.push({
                                    identifier: e.identifier,
                                    performanceDataViewType: null, // special usage CriteriaIdType. must be null
                                    displayNameCriteriaIdType:
                                        CriteriaIdType.TEAM_ID,
                                    outputCriteriaType:
                                        CriteriaIdType.TEAM_ROLE_ID,
                                    dataId,
                                    outputCriteriaTypes: [
                                        CriteriaIdType.TEAM_ROLE_ID,
                                    ],
                                    outputCriteriaTypeArgs: {
                                        [CriteriaIdType.TEAM_ROLE_ID]: dataId,
                                    },
                                });
                        }

                        if (ee === CriteriaIdType.USER_ID) {
                            const a = parentLevelsDataMap.find(
                                (eee) =>
                                    eee.outputCriteriaType ===
                                    CriteriaIdType.USER_ID,
                            );
                            const dataId = dataIdForInputCriteriaIdType(
                                e,
                                ee,
                                a,
                            );
                            dataId != null &&
                                levelsDataMap.push({
                                    identifier: e.identifier,
                                    performanceDataViewType:
                                        PerformanceDataViewType.USER,
                                    displayNameCriteriaIdType: null,
                                    outputCriteriaType: CriteriaIdType.USER_ID,
                                    dataId,
                                    outputCriteriaTypes: [
                                        CriteriaIdType.USER_ID,
                                    ],
                                    outputCriteriaTypeArgs: {
                                        [CriteriaIdType.USER_ID]: dataId,
                                    },
                                });
                        }

                        if (ee === CriteriaIdType.SUPER_GROUP_ID) {
                            const a = parentLevelsDataMap.find(
                                (eee) =>
                                    eee.outputCriteriaType ===
                                    CriteriaIdType.SUPER_GROUP_ID,
                            );
                            const dataId = dataIdForInputCriteriaIdType(
                                e,
                                ee,
                                a,
                            );
                            dataId != null &&
                                levelsDataMap.push({
                                    identifier: e.identifier,
                                    performanceDataViewType:
                                        PerformanceDataViewType.SUPER_GROUP,
                                    displayNameCriteriaIdType: null,
                                    outputCriteriaType:
                                        CriteriaIdType.SUPER_GROUP_ID,
                                    dataId,
                                    outputCriteriaTypes: [
                                        CriteriaIdType.SUPER_GROUP_ID,
                                    ],
                                    outputCriteriaTypeArgs: {
                                        [CriteriaIdType.SUPER_GROUP_ID]: dataId,
                                    },
                                });
                        }

                        if (
                            ee === CriteriaIdType.TEAM_ID ||
                            ee === CriteriaIdType.HIERARCHY
                        ) {
                            const a = parentLevelsDataMap.find(
                                (eee) =>
                                    eee.outputCriteriaType ===
                                        CriteriaIdType.TEAM_ID ||
                                    eee.outputCriteriaType ===
                                        CriteriaIdType.USER_ID,
                            );
                            const dataId = dataIdForInputCriteriaIdType(
                                e,
                                ee,
                                a,
                            );
                            dataId != null &&
                                levelsDataMap.push({
                                    identifier: e.identifier,
                                    performanceDataViewType:
                                        PerformanceDataViewType.TEAM_SUPER_GROUP,
                                    displayNameCriteriaIdType: null,
                                    outputCriteriaType: CriteriaIdType.TEAM_ID,
                                    dataId,
                                    outputCriteriaTypes: [
                                        CriteriaIdType.TEAM_ID,
                                    ],
                                    outputCriteriaTypeArgs: {
                                        [CriteriaIdType.TEAM_ID]: dataId,
                                    },
                                });
                        }
                    });
            });
        };
        this._dataSource.didMakeRow = (
            level: ConfigPathLevel,
            userInfo: HierarchyUserInfoItem,
            levelDataMapRole: LevelDataMap[],
        ): void => {
            if (!userInfo) return;
            let uRoleId;
            let tRoleId;
            if (level.displayNameCriteriaIdType === CriteriaIdType.USER_ID) {
                uRoleId = userInfo.roleId;
                const f = this.roleHierarchy.find(
                    (e) =>
                        e.startDate &&
                        e.endDate &&
                        this.eventDate.getTime() >= e.startDate.getTime() &&
                        this.eventDate.getTime() <= e.endDate.getTime() &&
                        e.parentRoleId === uRoleId,
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                tRoleId = f && f.roleId;
            }
            if (level.displayNameCriteriaIdType === CriteriaIdType.TEAM_ID) {
                tRoleId = userInfo.roleId;
                const f = this.roleHierarchy.find((e) => e.roleId === tRoleId);
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                uRoleId = f && f.parentRoleId;
            }
            uRoleId != null &&
                levelDataMapRole.push({
                    identifier: level.identifier,
                    performanceDataViewType: null,
                    displayNameCriteriaIdType: level.displayNameCriteriaIdType,
                    outputCriteriaType: CriteriaIdType.USER_ROLE_ID,
                    dataId: uRoleId,
                    outputCriteriaTypes: [CriteriaIdType.USER_ROLE_ID],
                    outputCriteriaTypeArgs: {
                        [CriteriaIdType.USER_ROLE_ID]: uRoleId,
                    },
                });
            tRoleId != null &&
                levelDataMapRole.push({
                    identifier: level.identifier,
                    performanceDataViewType: null,
                    displayNameCriteriaIdType: level.displayNameCriteriaIdType,
                    outputCriteriaType: CriteriaIdType.TEAM_ROLE_ID,
                    dataId: tRoleId,
                    outputCriteriaTypes: [CriteriaIdType.TEAM_ROLE_ID],
                    outputCriteriaTypeArgs: {
                        [CriteriaIdType.TEAM_ROLE_ID]: tRoleId,
                    },
                });
        };
        this._dataSource.makeColumns(
            this.viewPillar,
            this.viewMetric,
            this.pillars,
            this.columns,
            this.metrics,
            this.view.pillarHeadersExpandedInGrid,
            this.view.pillarsInGrid !== PillarsInGrid.ALL,
        );
        // this._dataSource.applyHiddenColumns(this.pillarSelection, this.columnSelection);

        const periodTypeOpt = PeriodTypeOpts.find(
            (e) => e.value === this.periodType,
        );
        const periodValue = periodTypeOpt
            ? this._translateService.instant(periodTypeOpt.tlKey)
            : 'N/A';
        const dateRangeValue =
            getEventDateValue(
                this.cacheDates,
                this.eventDate,
                this.periodType,
                this._datePipe,
            ) || 'N/A';
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        this._dataSource.columns[0].ctx.name = `${periodValue}\n${dateRangeValue}`;

        try {
            this._renderer = Controller.usingDef<
                DivRenderer,
                DivRendererOptions
            >(
                DivRenderer,
                {
                    ctx: {
                        defaultGridTpl: this._defaultGrid.tpl,
                        defaultRowTpl: this._defaultRow.tpl,
                        defaultCellTpl: this._defaultCell.tpl,
                        trendVisible: this.trendVisible,
                        showThresholdColours: this.view.showThresholdColours,
                        showTrendArrows: this.view.showTrendArrows,
                        pendingColumnMovementReset: false,
                        refs: this._refs,
                    },
                },
                {
                    columns: this._dataSource.columns,
                    defaultRenderFn: render,
                    horizontalScrollPadding: 4000,
                },
            );
            this._grid = this._renderer.gridNode;
            this._renderer.layoutt
                .asObservable()
                .pipe(
                    debounceTime(1000),
                    filter(() => !!this._renderer),
                    tap(() => {
                        void this._renderer.autoSizeColumns([
                            ColumnID.NODE.toString(),
                        ]);
                        this._layout();
                    }),
                )
                .subscribe();
            void this._renderer
                .layout()
                .then(() => {
                    this._dataSource.applyHiddenColumns(
                        this.pillarSelection,
                        this.columnSelection,
                        this._renderer,
                    );
                    interval(100)
                        .pipe(
                            takeWhile(() => {
                                try {
                                    return (
                                        this._container.nativeElement.children
                                            .length === 0
                                    );
                                } catch {
                                    return true;
                                }
                            }),
                        )
                        .subscribe({
                            complete: () => {
                                void this._renderer
                                    .autoSizeColumnTree()
                                    .then(() => {
                                        this._dataSource.columnsFlatten
                                            .filter((e) => !e.hidden)
                                            .forEach(
                                                (e) => (e.ctx.autoSized = true),
                                            );
                                    });
                            },
                        });
                })
                .then(() => {
                    this._renderer.contentColumns.columns
                        .find(
                            (e) =>
                                e.cells.at(0).context.c.ctx.type ===
                                ColumnType.METRIC,
                        )
                        .drag.asObservable()
                        .pipe(
                            filter(
                                (e) => e.which === 'drop' && e.drag !== e.drop,
                            ),
                            tap((e) => {
                                let comps = e.columnsSrc.filter(
                                    (ee: Column<ColumnContext>) =>
                                        ee.ctx.parentId === e.drag.context.c.id,
                                );
                                this._renderer.moveColumns(
                                    comps.map((ee) => ee.id),
                                    e.drag.context.c.id,
                                    'after',
                                );

                                comps = e.columnsSrc.filter(
                                    (ee: Column<ColumnContext>) =>
                                        ee.ctx.parentId === e.drop.context.c.id,
                                );
                                this._renderer.moveColumns(
                                    comps.map((ee) => ee.id),
                                    e.drop.context.c.id,
                                    'after',
                                );

                                this._dataSource.columnsFlatten =
                                    flattenArrayWithObj(
                                        this._dataSource.columns,
                                        'children',
                                    );

                                if (
                                    e.drag.context.ctx.hasPillarHandle ||
                                    e.drop.context.ctx.hasPillarHandle ||
                                    e.columnsDest !== e.columnsSrc
                                ) {
                                    this._dataSource.reassignPillarHandles();
                                }
                                if (e.columnsDest !== e.columnsSrc) {
                                    this._dataSource.collapseAllPillars();
                                    this._grid.context.ctx.pendingColumnMovementReset =
                                        true;
                                }
                                // if (e.drop.context.ctx.hasPillarHandle || !this.state.lastToggleColumnState) {
                                if (e.columnsDest !== e.columnsSrc) {
                                    this._dataSource.applyHiddenColumns(
                                        this.pillarSelection,
                                        this.columnSelection,
                                        this._renderer,
                                    );
                                    void this._renderer.layout();
                                } else {
                                    // store.dispatch(gridResetToggleColumn());
                                }
                            }),
                        )
                        .subscribe();
                });
            this._epicBridge.renderer = this._renderer;
        } catch (e) {
            console.log(e);
        }

        this._getData();
    }

    private _deflate(): void {
        store.dispatch(gridRetrieveDataCancel());

        try {
            this._grid.renderContext.element.parentElement.removeChild(
                this._grid.renderContext.element,
            );
        } catch {}
        try {
            this._renderer.destroy(this._grid);
        } catch (e) {
            console.log(e);
        }
        this._grid = null;
        this._renderer = null;
        this._epicBridge.renderer = null;
    }

    private _layout(): void {
        const leftVp = this._renderer.columns.left.viewport;
        this._contentRule.prop(
            'left',
            `${!this.trendHideFirstGridColumn ? leftVp.clientWidth : 0}px`,
        );

        this.layout.emit(this._contentRule.selector.substr(1));
    }

    private _layoutAndAutoSize(
        columns: Array<Column<ColumnContext>>,
        force?: boolean,
    ): void {
        void this._renderer
            .layout()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                const as = columns?.filter(
                    (e) => (force || !e.ctx.autoSized) && !e.hidden,
                );
                return (
                    as &&
                    autoSizeColumnTree(
                        this._renderer.contentColumns.columns.children,
                        this._renderer,
                        as,
                    ).then(() => {
                        as.forEach((e) => (e.ctx.autoSized = true));
                    })
                );
            });
    }
}
